import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faClock, faListUl } from '@fortawesome/free-solid-svg-icons';
import SEO from '../components/SEO';
import LocalizedLink from '../components/LocalizedLink';
import MarkdownRenderer from '../components/MarkdownRenderer';
import {
  getCourseBySlug,
  getCurriculum,
  getLesson,
  getLessonIndex,
} from '../data/courseLessons';

const CourseLessonPage: React.FC = () => {
  const { courseSlug, lessonSlug } = useParams<{ courseSlug: string; lessonSlug: string }>();
  const { t } = useTranslation();

  const course = courseSlug ? getCourseBySlug(courseSlug) : undefined;
  const curriculum = course ? getCurriculum(course.id) : undefined;
  const lesson =
    course && lessonSlug ? getLesson(course.id, lessonSlug) : undefined;
  const index = course && lessonSlug ? getLessonIndex(course.id, lessonSlug) : -1;
  const lessons = curriculum?.lessons || [];
  const prev = index > 0 ? lessons[index - 1] : undefined;
  const next = index >= 0 && index < lessons.length - 1 ? lessons[index + 1] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lessonSlug]);

  if (!course || course.status !== 'available' || !curriculum || !lesson || index < 0) {
    return <Navigate to={courseSlug ? `/learn/${courseSlug}` : '/learn'} replace />;
  }

  const courseTitle = t(course.titleKey);

  return (
    <>
      <SEO
        title={`${lesson.title} — ${courseTitle} | GlobalPreps`}
        description={lesson.summary}
        keywords={`${lesson.title}, ${courseTitle}, AI course lesson`}
        url={`/learn/${course.slug}/${lesson.slug}`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'LearningResource',
          name: lesson.title,
          description: lesson.summary,
          learningResourceType: 'Lesson',
          timeRequired: `PT${lesson.durationMinutes}M`,
          isPartOf: {
            '@type': 'Course',
            name: courseTitle,
            url: `https://globalpreps.com/learn/${course.slug}`,
          },
          provider: { '@type': 'Organization', name: 'GlobalPreps' },
        }}
        breadcrumbs={[
          { name: t('nav.home'), url: '/' },
          { name: t('nav.learn'), url: '/learn' },
          { name: courseTitle, url: `/learn/${course.slug}` },
          { name: lesson.title, url: `/learn/${course.slug}/${lesson.slug}` },
        ]}
      />

      <div className="gp-page">
        <div className="gp-container pt-6 sm:pt-8 pb-10">
          <div className="grid lg:grid-cols-12 gap-5">
            <aside className="lg:col-span-3 order-2 lg:order-1">
              <div className="gp-card lg:sticky lg:top-16">
                <LocalizedLink to={`/learn/${course.slug}`} className="gp-back-link mb-3 text-xs">
                  <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                  {courseTitle}
                </LocalizedLink>
                <h2 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: 'var(--gp-ink-subtle)' }}>
                  <FontAwesomeIcon icon={faListUl} aria-hidden="true" />
                  {t('learn.curriculum')}
                </h2>
                <nav aria-label={t('learn.curriculum')}>
                  <ol className="space-y-1 list-none p-0 m-0">
                    {lessons.map((item, i) => {
                      const active = item.slug === lesson.slug;
                      return (
                        <li key={item.id}>
                          <LocalizedLink
                            to={`/learn/${course.slug}/${item.slug}`}
                            className={`block rounded-md px-2 py-1.5 text-xs transition-colors no-underline ${
                              active ? 'gp-dropdown-item-active font-semibold' : 'gp-dropdown-item'
                            }`}
                          >
                            <span className="opacity-60 mr-1">{i + 1}.</span>
                            {item.title}
                          </LocalizedLink>
                        </li>
                      );
                    })}
                  </ol>
                </nav>
              </div>
            </aside>

            <article className="lg:col-span-9 order-1 lg:order-2">
              <header className="mb-5">
                <p className="text-xs gp-text-muted mb-1">
                  {t('learn.lessonProgress', { current: index + 1, total: lessons.length })}
                </p>
                <h1 className="gp-heading-lg mb-2">{lesson.title}</h1>
                <p className="text-sm gp-text-muted mb-2">{lesson.summary}</p>
                <span className="text-xs gp-text-muted inline-flex items-center gap-1">
                  <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                  {t('learn.readMinutes', { count: lesson.durationMinutes })}
                </span>
              </header>

              <div className="gp-card prose-course mb-5">
                <MarkdownRenderer content={lesson.content} />
              </div>

              <nav
                className="flex flex-col sm:flex-row gap-2 sm:justify-between"
                aria-label={t('learn.lessonNav')}
              >
                {prev ? (
                  <LocalizedLink to={`/learn/${course.slug}/${prev.slug}`} className="gp-btn-ghost">
                    <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                    {prev.title}
                  </LocalizedLink>
                ) : (
                  <span />
                )}
                {next ? (
                  <LocalizedLink to={`/learn/${course.slug}/${next.slug}`} className="gp-btn-primary sm:ml-auto">
                    {next.title}
                    <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                  </LocalizedLink>
                ) : (
                  <LocalizedLink to="/certifications" className="gp-btn-accent sm:ml-auto">
                    {t('learn.startPracticing')}
                    <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                  </LocalizedLink>
                )}
              </nav>
            </article>
          </div>
        </div>
      </div>
    </>
  );
};

export default CourseLessonPage;
